import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { cn } from 'helpers/utils/classnames';
import { gtmImageScroll } from 'helpers/utils/gtm-events/gtmInteraction';
import { desktop } from 'helpers/utils/screensizes';
import Chevron from 'icons/chevron-down';
import VideoPlay from 'icons/video-play';
import { ProductAsset } from '../../../../../../types/product/Product';

interface ProductGalleryProps {
  assets: ProductAsset[];
  isQuickView?: boolean;
  variantSku?: string;
  productName?: string;
  onImageChange: (imageUrl: string) => void;
}

const carouselDesktopOptions: EmblaOptionsType = {
  align: 'start',
  axis: 'y',
};

const carouselMobileOptions: EmblaOptionsType = {
  align: 'center',
  axis: 'x',
};

const SUPPORTED_TRANSCODINGS = [
  { urlSuffix: 'mp4_480p', type: 'video/mp4' },
  { urlSuffix: 'mp4_720p', type: 'video/mp4' },
  { urlSuffix: 'webm_480p', type: 'video/webm' },
  { urlSuffix: 'webm_720p', type: 'video/webm' },
];

const ProductGallery = ({
  assets,
  isQuickView = false,
  productName,
  variantSku,
  onImageChange,
}: ProductGalleryProps) => {
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);
  const [options, setOptions] = useState(carouselDesktopOptions);
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [isDesktop] = useMediaQuery(desktop);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setOptions(isDesktop ? carouselDesktopOptions : carouselMobileOptions);
    if (emblaApi) {
      if (!isDesktop) {
        emblaApi.on('select', setDraggedSlide);
      } else {
        emblaApi.off('select', setDraggedSlide);
      }
    }
  }, [isDesktop, emblaApi]);

  const setDraggedSlide = useCallback((emblaApi: any) => {
    setSelectedAssetIndex(emblaApi.selectedScrollSnap());
  }, []);

  const handleSelectPrev = () => {
    if (!emblaApi) return;
    //Loop back to end of carousel
    if (selectedAssetIndex == 0) {
      setSelectedAssetIndex(assets.length - 1);
      emblaApi.scrollTo(assets.length - 1);
      return;
    }
    emblaApi.scrollPrev();
    setSelectedAssetIndex((prevIndex) => prevIndex - 1);
  };

  const handleSelectNext = () => {
    if (!emblaApi) return;
    //Loop back to start of carousel
    if (selectedAssetIndex == assets.length - 1) {
      setSelectedAssetIndex(0);
      emblaApi.scrollTo(0);
      return;
    } else if (selectedAssetIndex >= 3) {
      emblaApi.scrollNext();
    }
    setSelectedAssetIndex((prevIndex) => prevIndex + 1);
  };

  useEffect(() => {
    if (isDesktop) {
      isDesktop && videoRef.current?.focus();
    }
    onImageChange(assets[selectedAssetIndex]?.src);
  }, [selectedAssetIndex]);

  useEffect(() => {
    if (selectedAssetIndex >= assets.length) {
      setSelectedAssetIndex(0);
      emblaApi?.scrollTo(0);
    }
  }, [assets, selectedAssetIndex]);

  const positiveModulo = (n: number, m: number) => ((n % m) + m) % m;

  return (
    assets &&
    assets.length > 0 && (
      <div className="flex grow flex-col gap-8 lg:flex-row" data-testid="product-gallery">
        <div
          data-testid="selected-asset"
          className={cn('relative hidden aspect-1 w-full min-w-0 lg:block', {
            'xl:h-[672px] xl:w-[672px]': !isQuickView,
            'h-max w-full': isQuickView,
          })}
        >
          {assets[selectedAssetIndex]?.type === 'IMAGE' ? (
            <Image
              src={assets[selectedAssetIndex].src}
              alt={'Selected product image'}
              fill={true}
              loader={amplienceImageLoader}
              className="rounded-md object-contain"
              sizes="50vw"
              priority={true}
            />
          ) : assets[selectedAssetIndex]?.type === 'VIDEO' ? (
            <video
              ref={videoRef}
              aria-label="Selected product video"
              controls
              crossOrigin="anonymous"
              width="720"
              muted={true}
              autoPlay={true}
              tabIndex={0}
              data-testid="product-video-desktop"
            >
              {SUPPORTED_TRANSCODINGS.map((transcoding, index) => (
                <source
                  key={index}
                  src={`https://cdn.media.amplience.net/v/shadesoflight/${assets[selectedAssetIndex]?.src}/${transcoding.urlSuffix}`}
                  type={transcoding.type}
                />
              ))}
              <track
                kind="subtitles"
                src={`https://shadesoflight.a.bigcontent.io/v1/static/${assets[selectedAssetIndex]?.src}-captions-en_US`}
                srcLang="en"
                label="English Subtitles"
                default
              ></track>
              Your browser does not support the video tag.
            </video>
          ) : (
            <></>
          )}
        </div>

        <section className="flex aspect-1 h-full flex-col items-center justify-start lg:flex lg:h-auto lg:w-[100px]">
          <button
            onClick={() => handleSelectPrev()}
            className="hidden h-5 w-5 lg:flex"
            aria-label={`Previous ${assets[positiveModulo(selectedAssetIndex - 1, assets.length)].type === 'IMAGE' ? 'image' : 'video'}`}
            data-testid="previous-image-button"
          >
            <Chevron className="rotate-180 fill-black" />
          </button>
          <div className={'flex h-full w-full overflow-hidden lg:h-auto lg:w-[100px]'} ref={emblaRef}>
            <div
              className={cn('flex h-full w-full items-center lg:flex-col', {
                'lg:h-[424px]': !isQuickView,
                'lg:h-[356px]': isQuickView,
              })}
            >
              {assets.map((asset, index) => (
                <button
                  aria-label={`Select product ${asset.type === 'IMAGE' ? 'image' : 'video'}`}
                  className={cn(
                    `relative h-full w-full flex-shrink-0 flex-grow-0 basis-full lg:my-2 lg:basis-auto ${selectedAssetIndex == index ? 'lg:rounded-md lg:outline lg:outline-[1px] lg:outline-offset-2 lg:outline-gold' : ''}`,
                    {
                      'lg:h-[90px] lg:w-[90px]': !isQuickView,
                      'lg:h-[74px] lg:w-[74px]': isQuickView,
                    },
                  )}
                  onClick={() => {
                    if (variantSku && productName) {
                      gtmImageScroll({
                        click_action: 'view images',
                        item_id: variantSku,
                        media_name: `${productName} ${index}`,
                      });
                    }
                    setSelectedAssetIndex(index);
                    if (asset.type === 'VIDEO') {
                      videoRef.current?.focus();
                    }
                  }}
                  key={'gallery-asset-' + index}
                  data-testid={'gallery-asset-' + index}
                >
                  {asset.type === 'IMAGE' ? (
                    <Image
                      src={asset.src}
                      alt={'Product image'}
                      loader={amplienceImageLoader}
                      fill={true}
                      className={`rounded-md ${selectedAssetIndex == index ? '' : 'lg:opacity-50'}`}
                      sizes="(max-width: 744px) 100vw, (max-width: 1024px) 50vw, 20vw"
                    />
                  ) : isDesktop ? (
                    <>
                      <Image
                        src={`https://cdn.media.amplience.net/v/shadesoflight/${asset.src}`}
                        alt={'Product video'}
                        loader={amplienceImageLoader}
                        fill={true}
                        className={`rounded-md ${selectedAssetIndex == index ? '' : 'lg:opacity-50'}`}
                        sizes="(max-width: 744px) 100vw, (max-width: 1024px) 50vw, 20vw"
                      />
                      <VideoPlay className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </>
                  ) : (
                    <video
                      aria-label="Product video"
                      controls
                      crossOrigin="anonymous"
                      width="720"
                      muted={true}
                      autoPlay={true}
                      tabIndex={0}
                      data-testid="product-video-mobile"
                    >
                      {SUPPORTED_TRANSCODINGS.map((transcoding, index) => (
                        <source
                          key={index}
                          src={`https://cdn.media.amplience.net/v/shadesoflight/${asset.src}/${transcoding.urlSuffix}`}
                          type={transcoding.type}
                        />
                      ))}
                      <track
                        kind="subtitles"
                        src={`https://shadesoflight.a.bigcontent.io/v1/static/${asset.src}-captions-en_US`}
                        srcLang="en"
                        label="English Subtitles"
                        default
                      ></track>
                      Your browser does not support the video tag.
                    </video>
                  )}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => handleSelectNext()}
            className="hidden h-5 w-5 lg:flex"
            aria-label={`Next ${assets[positiveModulo(selectedAssetIndex + 1, assets.length)].type === 'IMAGE' ? 'image' : 'video'}`}
            data-testid="next-image-button"
          >
            <Chevron className="fill-black" />
          </button>
        </section>
        <section className="flex justify-center gap-2 lg:hidden" data-testid="dots-display">
          {assets.map((_, index) => (
            <button
              className={`h-3 w-3 rounded-full border-[1px] border-black ${selectedAssetIndex == index ? 'bg-black' : ''}`}
              key={'dot-' + index}
              data-testid={'dot-' + index}
            />
          ))}
        </section>
      </div>
    )
  );
};

export default ProductGallery;
